import { isFeatureActive } from '@helpers';
import { ga4AddToWishlistEvent } from '@common/Favorites/tracking';
import { post, get, del } from '@common/Http';
import { getCsrfToken } from '@common/Http/requestHandler';
import { hasInvalidDataFormat } from '@common/Product/helpers/productData';
import { getRealListValue } from '@common/Tracking/RealList';
import { ga4event } from '@helpers/dataLayer';
import { loadState, saveState } from '@helpers/localStorage';
import { error as DDErrorLogger } from '@helpers/logger';
import { loadSession, saveSession, removeSession } from '@helpers/sessionStorage';
import {
    API_FAVORITE_PRODUCT_REMOVE,
    API_FAVORITE_FAVORITES_LIST,
    API_FAVORITE_PRODUCT_ADD,
    API_FAVORITE_BRAND_ADD,
    API_FAVORITE_BRAND_REMOVE,
    API_FAVORITE_BRAND_REMOVE_ALL,
    API_FAVORITE_BRAND_LIST_FAVORITE_JSON
} from '@routes';
import {
    TOGGLE_RECENT_AS_FAVORITE,
    UPDATE_RECENTS_WITH_FAVORITES
} from '../../Recents/actions/actionsConstants';
import {
    FAVORITE_PRODUCTS_SESSION_CACHE_NAME, favoriteBrandsCacheName, favoriteBrandsSessionName, FAVORITE_PRODUCTS_CACHE_NAME
} from '../constants/cache';
import { getProductData } from '../helpers/dataHelper';
import {
    FETCH_FAVORITES_REQUEST,
    FETCH_FAVORITES_SUCCESS,
    FETCH_FAVORITES_FAILURE,
    ADD_FAVORITES_REQUEST,
    ADD_FAVORITES_SUCCESS,
    ADD_FAVORITES_FAILURE,
    DELETE_FAVORITES_REQUEST,
    DELETE_FAVORITES_SUCCESS,
    DELETE_FAVORITES_FAILURE,
    SET_FAVORITE_BRAND,
    ADD_FAVORITE_BRAND,
    REMOVE_FAVORITE_BRAND,
    SET_BRAND_FAVORITES,
    ADD_TO_FAVORITES_QUEUE,
    REMOVE_FROM_FAVORITES_QUEUE,
    DROP_FAVORITES,
    UPDATE_FAVORITES_VARIANTS_IDS,
    ADD_FAVORITE_VARIANT_ID
} from './actionsConstants';

const appPostMessage = (userEvent, productId, variantId) => {
    try {
        if (window?.webkit?.messageHandlers) {
            window.webkit.messageHandlers.cmsFavorites.postMessage({
                userEvent,
                productId,
                variantId
            });
        }

        if (/(android)/i.test(navigator.userAgent)) {
            if (typeof AddToFavouritesAndroidInterface !== 'undefined') {
                const dataToSend = JSON.stringify({
                    userEvent,
                    productId,
                    variantId
                });
                // eslint-disable-next-line no-undef
                AddToFavouritesAndroidInterface.sendFavouritesEvent(dataToSend);
            }
        }
    } catch (e) {
        // do nothing
    }
};

export const getData = () => {
    if (window.history.length <= 1) {
        sessionStorage.removeItem(FAVORITE_PRODUCTS_SESSION_CACHE_NAME);
    }

    return { type: FETCH_FAVORITES_REQUEST };
};

export const addData = () => ({ type: ADD_FAVORITES_REQUEST });

export const deleteData = () => ({ type: DELETE_FAVORITES_REQUEST });

export const getDataSuccess = response => (dispatch) => {
    saveSession(FAVORITE_PRODUCTS_SESSION_CACHE_NAME, Date.now());
    const products = response.data?.products || response.product_cards;

    dispatch({
        type: FETCH_FAVORITES_SUCCESS,
        payload: products
    });

    dispatch({
        type: UPDATE_RECENTS_WITH_FAVORITES,
        payload: products
    });

    dispatch({ type: UPDATE_FAVORITES_VARIANTS_IDS });
    saveState(FAVORITE_PRODUCTS_CACHE_NAME, products);
};

export const addDataSuccess = response => (dispatch) => {
    const product = response.data?.product || response.product_card?.[0];
    const variantId = product.variant_id || product.variants?.[0].id;

    dispatch({
        type: ADD_FAVORITES_SUCCESS,
        payload: product
    });

    dispatch({
        type: TOGGLE_RECENT_AS_FAVORITE,
        toggle: true,
        payload: variantId
    });

    dispatch({
        type: ADD_FAVORITE_VARIANT_ID,
        payload: variantId
    });
};

export const deleteDataSuccess = variantId => (dispatch) => {
    dispatch({
        type: DELETE_FAVORITES_SUCCESS,
        payload: variantId
    });

    dispatch({
        type: TOGGLE_RECENT_AS_FAVORITE,
        toggle: false,
        payload: variantId
    });

    dispatch({ type: UPDATE_FAVORITES_VARIANTS_IDS });
};

export const getDataError = err => ({ type: FETCH_FAVORITES_FAILURE, err });

export const addDataError = err => ({ type: ADD_FAVORITES_FAILURE, err });

export const deleteDataError = err => ({ type: DELETE_FAVORITES_FAILURE, err });

export const dropFavorites = () => (dispatch) => {
    removeSession(FAVORITE_PRODUCTS_SESSION_CACHE_NAME);
    dispatch({ type: DROP_FAVORITES });
};

const hasCacheSessionExpired = (cacheName) => {
    const session = loadSession(cacheName);
    let timeDifference = 11;

    if (typeof session !== 'undefined') {
        timeDifference = Math.floor((Date.now() - session) / 1000 / 60);
    }

    return timeDifference > 10;
};

export const fetchFavorites = (hasToBumpCache = false) => async (dispatch) => {
    const cachedFavoriteProducts = loadState(FAVORITE_PRODUCTS_CACHE_NAME);

    dispatch(getData());

    if (
        hasToBumpCache
        || hasCacheSessionExpired(FAVORITE_PRODUCTS_SESSION_CACHE_NAME)
        || isFeatureActive('T74590_mylists_no_cache')
        || hasInvalidDataFormat(cachedFavoriteProducts)
    ) {
        get(API_FAVORITE_FAVORITES_LIST)
            .then(response => dispatch(getDataSuccess(response)))
            .catch(error => dispatch(getDataError(error)));
    } else if (cachedFavoriteProducts) {
        dispatch({
            type: FETCH_FAVORITES_SUCCESS,
            payload: cachedFavoriteProducts
        });

        dispatch({ type: UPDATE_FAVORITES_VARIANTS_IDS });
    }
};

const addFavoritesTrackingActions = (productData) => {
    const {
        productId,
        variantId
    } = productData;

    ga4event('product.favorites.add', {
        productId,
        variantId
    });

    appPostMessage('addFavorite', productId, variantId);
};

export const addFavorites = (
    variant,
    itemsParameters = {},
    interactionOrigin
) => (dispatch) => {
    dispatch({ type: ADD_TO_FAVORITES_QUEUE, payload: variant });
    dispatch(addData());

    if (!getCsrfToken()) {
        DDErrorLogger('front_end.missing_csrf_token', { interactionOrigin });
    }

    post(API_FAVORITE_PRODUCT_ADD, {
        variant_id: variant,
        realList: getRealListValue()
    }).then((response) => {
        dispatch(addDataSuccess(response));

        ga4AddToWishlistEvent(
            itemsParameters,
            interactionOrigin,
            response?.add_to_wishlist_tracking
        );

        addFavoritesTrackingActions(getProductData(response, true));
    }).catch(error => dispatch(addDataError(error)))
        .finally(() => dispatch({ type: REMOVE_FROM_FAVORITES_QUEUE, payload: variant }));
};

const deleteFavoritesTrackingActions = ({ productId, variantId }) => {
    ga4event('product.favorites.remove', {
        productId,
        variantId
    });

    appPostMessage('removeFavorite', productId, variantId);
};

export const deleteFavorites = variant => (dispatch) => {
    dispatch({ type: ADD_TO_FAVORITES_QUEUE, payload: variant });
    dispatch(deleteData());

    del(API_FAVORITE_PRODUCT_REMOVE, {
        variant_ids: variant
    }).then((response) => {
        dispatch(deleteDataSuccess(variant));

        response.removed.forEach(({ variant_id: variantId, product_id: productId }) => {
            deleteFavoritesTrackingActions({ productId, variantId });
        });
    }).catch(error => dispatch(deleteDataError(error)))
        .finally(() => dispatch({ type: REMOVE_FROM_FAVORITES_QUEUE, payload: variant }));
};

export const setFavoriteBrand = brandData => dispatch => dispatch({
    type: SET_FAVORITE_BRAND,
    payload: brandData
});

export const addFavoriteBrand = brand => dispatch => dispatch({
    type: ADD_FAVORITE_BRAND,
    payload: brand
});

export const removeFavoriteBrand = brand => dispatch => dispatch({
    type: REMOVE_FAVORITE_BRAND,
    payload: brand
});

const setBrandFavoritesAction = brands => (dispatch) => {
    dispatch({
        type: SET_BRAND_FAVORITES,
        payload: brands
    });

    saveState(favoriteBrandsCacheName, brands);
};

export const fetchBrandFavorites = (visitorId, lastUpdateId) => async (dispatch) => {
    const cachedFavoriteBrands = loadState(favoriteBrandsCacheName);

    if (hasCacheSessionExpired(favoriteBrandsSessionName) || isFeatureActive('T74590_mylists_no_cache')) {
        try {
            const response = await get(API_FAVORITE_BRAND_LIST_FAVORITE_JSON, {
                visitorId,
                timestamp: lastUpdateId
            });

            dispatch(setBrandFavoritesAction(response));
            saveSession(favoriteBrandsSessionName, Date.now());
        } catch (error) {}
    } else if (cachedFavoriteBrands) {
        dispatch({
            type: SET_BRAND_FAVORITES,
            payload: cachedFavoriteBrands
        });
    }
};

export const addToBrandFavorites = (brandId, gender) => async (dispatch) => {
    try {
        const response = await post(API_FAVORITE_BRAND_ADD, {
            brandId,
            gender
        });

        dispatch(setBrandFavoritesAction(response));
    } catch (error) {}
};

export const deleteFromBrandFavorites = (brandId, gender) => async (dispatch) => {
    try {
        const response = await del(API_FAVORITE_BRAND_REMOVE, {
            brandId,
            gender
        });

        dispatch(setBrandFavoritesAction(response));
    } catch (error) {}
};

export const deleteAllBrandFavorites = () => async (dispatch) => {
    try {
        await del(API_FAVORITE_BRAND_REMOVE_ALL);

        dispatch(setBrandFavoritesAction());
    } catch (error) {}
};
